// HSAIcon.jsx
// This component is used to display icons in the HSA-Kit. It is a wrapper around MUI and FontAwesome icons.
// It uses the HSA theme to determine the color of the icon.
// It also supports displaying text as an icon.

// Copyright HS Analysis GmbH, 2024
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// MUI imports
import { makeStyles } from "@mui/styles";
import Add from "@mui/icons-material/Add";
import AllOutIcon from "@mui/icons-material/AllOut";
import CastIcon from "@mui/icons-material/Cast";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import FilterAlt from "@mui/icons-material/FilterAlt";
import FlipIcon from "@mui/icons-material/Flip";
import FolderIcon from "@mui/icons-material/Folder";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PinDropIcon from "@mui/icons-material/PinDrop";
import PolylineIcon from "@mui/icons-material/Polyline";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RateReviewIcon from "@mui/icons-material/RateReview";
import RedoIcon from "@mui/icons-material/Redo";
import Rotate90DegreesCwIcon from "@mui/icons-material/Rotate90DegreesCw";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import Save from "@mui/icons-material/Save";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import SortIcon from "@mui/icons-material/Sort";
import SyncLockIcon from "@mui/icons-material/SyncLock";
import UndoIcon from "@mui/icons-material/Undo";
import ViewQuilt from "@mui/icons-material/ViewQuilt";
import ZoomIn from "@mui/icons-material/ZoomIn";
import ZoomOut from "@mui/icons-material/ZoomOut";
import ZoomOutMap from "@mui/icons-material/ZoomOutMap";

// fontawsome icon imports
import {
  faBraille,
  faBrain,
  faCamera,
  faClone,
  faCube,
  faDownload,
  faDrawPolygon,
  faExchangeAlt,
  faFileDownload,
  faFillDrip,
  faHandPointer,
  faImages,
  faMousePointer,
  faPen,
  faPencilRuler,
  faPlayCircle,
  faPlus,
  faScroll,
  faSquareFull,
  faTable,
  faThLarge,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// HSA imports
import CustomSvgIcon from "../../globalComponents/CustomSvgIcon";
import { Checkbox } from "@mui/material";

const HSAIcon = ({ icon, active, size, text, color }) => {
  if (size === undefined) size = 34;
  if (size < 18) size = 18;
  const iconSize = size - 10 + "px";

  const classes = useStyles();
  const icons = {
    "3DPointCloudView": <FontAwesomeIcon icon={faBraille} />,
    "3DViewer": <FontAwesomeIcon icon={faCube} />,
    "3DVisualisation": <FontAwesomeIcon icon={faCube} />,
    Add: <Add style={{ fontSize: iconSize }} />,
    AdjustLayoutTool: <ViewQuilt style={{ fontSize: iconSize }} />,
    AICockpit: <FontAwesomeIcon icon={faBrain} />,
    AITrainingTool: <FontAwesomeIcon icon={faBrain} />,
    AllOutIcon: <FontAwesomeIcon icon={faTable} />,
    ApplyModelTool: <FontAwesomeIcon icon={faBrain} />,
    CommentTool: <RateReviewIcon style={{ fontSize: iconSize }} />,
    CopyRegionAnnotationTool: <FontAwesomeIcon icon={faClone} />,
    Delete: <FontAwesomeIcon icon={faTrash} />,
    DrawEllipseAnnotationTool: (
      <FontAwesomeIcon icon={faCircle} mask={["far"]} />
    ),
    DrawGrabCutAnnotationTool: (
      <CustomSvgIcon
        name="GrabCutIcon"
        width={size}
        height={size}
        stroke="currentColor"
        vertAlign="-4px"
      />
    ),
    DrawMagicWandAnnotationTool: <FontAwesomeIcon icon={faPen} />,
    DrawPixelAnnotationTool: <FontAwesomeIcon icon={faPen} />,
    DrawRectangleAnnotationTool: (
      <CheckBoxOutlineBlank style={{ fontSize: iconSize }} />
    ),
    DrawRegionAnnotationTool: <FontAwesomeIcon icon={faDrawPolygon} />,
    DrawSAMAnnotationTool: (
      <CustomSvgIcon
        name="SAMIcon"
        width={size}
        height={size}
        stroke="currentColor"
      />
    ),
    DrawRegionGrowingAnnotationTool: (
      <AllOutIcon style={{ fontSize: iconSize }} />
    ),
    Edit: <FontAwesomeIcon icon={faPen} />,
    FileDownload: <FontAwesomeIcon icon={faFileDownload} />,
    FilterTool: <FilterAlt style={{ fontSize: iconSize }} />,
    FilterAnnotationsTool: <SortIcon style={{ fontSize: iconSize }} />,
    FillHoleAnnotationTool: <FontAwesomeIcon icon={faFillDrip} />,
    Flip: <FlipIcon style={{ fontSize: iconSize }} />,
    FlipDown: <FlipIcon style={{ fontSize: iconSize, rotate: "-90deg" }} />,
    Folder: <FolderIcon style={{ fontSize: iconSize }} />,
    FullscreenExit: <FullscreenExitIcon style={{ fontSize: iconSize }} />,
    Fullscreen: <FullscreenIcon style={{ fontSize: iconSize }} />,
    GalleryTool: <FontAwesomeIcon icon={faImages} />,
    GalleryTrainingTool: <FontAwesomeIcon icon={faImages} />,
    GridAnnotationTool: <FontAwesomeIcon icon={faTable} />,
    GridTool: <FontAwesomeIcon icon={faThLarge} />,
    HeatmapTool: (
      <CustomSvgIcon
        name="HeatmapIcon"
        width={size}
        height={size}
        stroke="currentColor"
        vertAlign="-4px"
      />
    ),
    LandmarkTool: <PinDropIcon style={{ fontSize: iconSize }} />,
    MoreVertIcon: <MoreVertIcon style={{ fontSize: iconSize }} />,
    OtherTools: <FontAwesomeIcon icon={faThLarge} />,
    PassiveLearningTool: <FontAwesomeIcon icon={faPencilRuler} />,
    PlayCircle: <FontAwesomeIcon icon={faPlayCircle} />,
    PlotNearestTool: <ScatterPlotIcon style={{ fontSize: iconSize }} />,
    Plus: <FontAwesomeIcon icon={faPlus} />,
    PolyManipulationTool: <PolylineIcon style={{ fontSize: iconSize }} />,
    RadioButtonCheckedIcon: (
      <RadioButtonCheckedIcon style={{ fontSize: iconSize }} />
    ),
    RateReviewIcon: <RateReviewIcon style={{ fontSize: iconSize }} />,
    Redo: <RedoIcon style={{ fontSize: iconSize }} />,
    ResultTab: <FontAwesomeIcon icon={faTable} />,
    RoiResizeTool: <RadioButtonCheckedIcon style={{ fontSize: iconSize }} />,
    RoiTab: <FontAwesomeIcon icon={faPencilRuler} />,
    Rotate1Icon: <SyncLockIcon style={{ fontSize: iconSize }} />,
    Rotate90Icon: <Rotate90DegreesCwIcon style={{ fontSize: iconSize }} />,
    RotateLeft: <RotateLeftIcon style={{ fontSize: iconSize }} />,
    RotateRight: <RotateRightIcon style={{ fontSize: iconSize }} />,
    SaveImagesTool: <FontAwesomeIcon icon={faDownload} />,
    Save: <Save style={{ fontSize: iconSize }} />,
    SaveTool: <Save style={{ fontSize: iconSize }} />,
    ScatterPlotIcon: <ScatterPlotIcon style={{ fontSize: iconSize }} />,
    Scroll: <FontAwesomeIcon icon={faScroll} />,
    SelectAll: <SelectAllIcon style={{ fontSize: iconSize }} />,
    SelectRegionAnnotationTool: <FontAwesomeIcon icon={faMousePointer} />,
    Cast: <CastIcon style={{ fontSize: iconSize }} />,
    ShowALTabTool: <FontAwesomeIcon icon={faBrain} />,
    ScreenshotTool: <FontAwesomeIcon icon={faCamera} />,
    SortIcon: <SortIcon style={{ fontSize: iconSize }} />,
    Text: (
      <div className={active ? classes.textIconActive : classes.textIcon}>
        {text}
      </div>
    ),
    TilesClassificationTool: <FontAwesomeIcon icon={faHandPointer} />,
    TilesTool: <FontAwesomeIcon icon={faSquareFull} />,
    Toggle: <FontAwesomeIcon icon={faExchangeAlt} />,
    Undo: <UndoIcon style={{ fontSize: iconSize }} />,
    ViewQuilt: <ViewQuilt style={{ fontSize: iconSize }} />,
    ZoomFitTool: <ZoomOutMap style={{ fontSize: iconSize }} />,
    ZoomInOutTool: <ZoomIn style={{ fontSize: iconSize }} />,
    ZoomOriginalTool: (
      <div className={active ? classes.textIcon : classes.textIconActive}>
        1:X
      </div>
    ),
    ZoomOut: <ZoomOut style={{ fontSize: iconSize }} />,
  };

  const containerStyle = {
    width: size,
    height: size,
    fontSize: iconSize,
  };
  if (color) containerStyle.color = color;

  return (
    <span className={active ? classes.active : classes.inactive}>
      {icons[icon] ? (
        <div style={containerStyle} className={classes.iconContainer}>
          {icons[icon]}
        </div>
      ) : (
        <Checkbox checked={active} />
      )}
    </span>
  );
};

HSAIcon.propTypes = {
  icon: PropTypes.string.isRequired,
  active: PropTypes.bool,
  size: PropTypes.number,
  text: PropTypes.string,
  color: PropTypes.string,
};

export default HSAIcon;

const useStyles = makeStyles((theme) => ({
  iconContainer: {
    display: "grid",
    placeItems: "center",
    margin: "0 auto",
  },
  textIcon: {
    display: "inline-block",
    fontSize: 18,
    fontWeight: "bold",
    color: theme.palette.primary.inactive,
  },
  textIconActive: {
    display: "inline-block",
    fontSize: 18,
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  active: {
    color: theme.palette.primary.main,
  },
  inactive: {
    color: theme.palette.primary.inactive,
  },
}));
